import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ExcelUploadModal from './ExcelUploadModal';
import $ from 'jquery';
import 'datatables.net-dt/js/dataTables.dataTables.js';
import 'datatables.net-responsive-dt/js/responsive.dataTables.js';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';
import { API_URL } from '..';

const StockListLayer = ({ jobsData = [] }) => {
  const history = useNavigate();
  const [currentUser, setCurrentUser] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [expandedMobileCards, setExpandedMobileCards] = useState({});
  const [mobilePage, setMobilePage] = useState(1);
  const [mobileLimit, setMobileLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const truncateText = (text, maxLength = 28) => {
    if (!text) return 'N/A';
    const value = String(text);
    return value.length > maxLength ? value.slice(0, maxLength) + '…' : value;
  };

  const formatMoney = (value) => {
    const amount = Number(value || 0);
    return amount.toFixed(2);
  };

  const filteredJobsData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return jobsData;

    return jobsData.filter((stock) => {
      const searchable = [
        stock?.title,
        stock?.productName,
        stock?.sku,
        stock?.quantity,
        stock?.trackInventory ? 'yes track inventory' : 'no not tracked',
        stock?.costPrice,
        stock?.sellPrice,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [jobsData, searchQuery]);

  const toggleMobileDetails = (stockId) => {
    setExpandedMobileCards((prev) => ({
      ...prev,
      [stockId]: !prev[stockId],
    }));
  };

  const handleDelete = async (id) => {
    const headersList = {
      Accept: '*/*',
      'Content-Type': 'application/json',
    };

    const response = await fetch(API_URL + `/api/invoice/stock/single-delete/${id}`, {
      method: 'DELETE',
      headers: headersList,
    });

    const data = await response.json();

    if (data.message === 'success') {
      window.location.reload();
    }
  };

  const handleSyncCRM = async () => {
    const ghlToken = localStorage.getItem('tokenID');
    const altId = localStorage.getItem('locationID');
    const locationId = localStorage.getItem('locationID');

    if (!ghlToken) {
      setSyncResult({ type: 'error', msg: 'GHL API key not found in localStorage (key: ghlApiKey)' });
      return;
    }

    if (!altId) {
      setSyncResult({ type: 'error', msg: 'GHL Location (altId) not found in localStorage (key: ghlAltId)' });
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      const res = await fetch(`${API_URL}/api/invoice/stock/sync-crm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locationId, ghlToken, altId }),
      });

      const data = await res.json();

      if (res.ok) {
        setSyncResult({
          type: 'success',
          msg: `${data.message} (${data.synced} new, ${data.skipped} refreshed of ${data.total} total)`,
        });

        setTimeout(() => window.location.reload(), 2000);
      } else {
        setSyncResult({ type: 'error', msg: data.message || 'Sync failed' });
      }
    } catch (err) {
      setSyncResult({ type: 'error', msg: `Network error: ${err.message}` });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    setMobilePage(1);
    setExpandedMobileCards({});
  }, [jobsData, searchQuery, mobileLimit]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);

    if ($.fn.DataTable.isDataTable('#dataTable')) {
      $('#dataTable').DataTable().clear().destroy();
      $('#dataTable tbody').off('click');
    }

    const table = $('#dataTable').DataTable({
      data: filteredJobsData,
      responsive: true,
      scrollX: false,
      autoWidth: false,
      pageLength: 10,
      order: [[0, 'desc']],
      dom: 'rt<"stock-datatable-footer"lip>',
      columnDefs: [
        { responsivePriority: 1, targets: 0 },
        { responsivePriority: 2, targets: 4 },
        { responsivePriority: 3, targets: 2 },
        { responsivePriority: 4, targets: 3 },
      ],
      columns: [
        {
          data: null,
          render: function (data, type, row) {
            const title = row.title || 'N/A';
            const shortTitle = title.length > 38 ? title.slice(0, 38) + '…' : title;
            const sku = row.sku ? `<div class="stock-sku-text">${row.sku}</div>` : '';
            return `<div class="stock-title-cell" title="${title}">${shortTitle}</div>${sku}`;
          },
        },
        {
          data: 'productName',
          render: function (data) {
            if (!data) return '<span class="text-muted">—</span>';
            const short = data.length > 36 ? data.slice(0, 36) + '…' : data;
            return `<span title="${data}">${short}</span>`;
          },
        },
        {
          data: 'quantity',
          render: function (data) {
            return `<span class="stock-qty-pill">${data ?? 0}</span>`;
          },
        },
        {
          data: 'trackInventory',
          render: function (data) {
            return data
              ? '<span class="stock-track-badge stock-track-yes">Yes</span>'
              : '<span class="stock-track-badge stock-track-no">No</span>';
          },
        },
        {
          data: null,
          render: function (data, type, row) {
            return `
              <div class="dropdown stock-actions-dropdown">
                <button class="stock-action-toggle dropdown-toggle" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport" data-bs-display="dynamic" aria-expanded="false">
                  Actions
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a style="cursor:pointer" class="dropdown-item view-btn" data-id="${row.id}"><i class="ph ph-eye me-2"></i>View</a></li>
                  <li><a style="cursor:pointer" class="dropdown-item edit-btn" data-id="${row.id}"><i class="ph ph-pencil me-2"></i>Edit</a></li>
                  <li><hr class="dropdown-divider"></li>
                  <li><a style="cursor:pointer" class="dropdown-item text-danger delete-btn" data-id="${row.id}"><i class="ph ph-trash me-2"></i>Delete</a></li>
                </ul>
              </div>
            `;
          },
          title: 'Actions',
          orderable: false,
          width: '120px',
        },
      ],
    });

    $('#dataTable tbody').on('click', '.edit-btn', function () {
      const id = $(this).data('id');
      history(`/edit-stock/${id}`);
    });

    $('#dataTable tbody').on('click', '.view-btn', function () {
      const id = $(this).data('id');
      history(`/view-stock/${id}`);
    });

    $('#dataTable tbody').on('click', '.delete-btn', function () {
      const id = $(this).data('id');
      handleDelete(id);
    });

    return () => {
      $('#dataTable tbody').off('click');

      if ($.fn.DataTable.isDataTable('#dataTable')) {
        table.destroy();
      }
    };
  }, [filteredJobsData, history]);

  const totalMobilePages = Math.max(1, Math.ceil(filteredJobsData.length / mobileLimit));
  const safeMobilePage = Math.min(mobilePage, totalMobilePages);
  const mobileStartIndex = (safeMobilePage - 1) * mobileLimit;
  const mobileEndIndex = Math.min(mobileStartIndex + mobileLimit, filteredJobsData.length);
  const paginatedMobileData = filteredJobsData.slice(mobileStartIndex, mobileEndIndex);

  const handleMobileLimitChange = (value) => {
    setMobileLimit(value);
    setMobilePage(1);
    setExpandedMobileCards({});
  };

  const renderMobileActions = (stock) => (
    <div className="dropdown mobile-stock-action-dropdown">
      <button
        className="mobile-stock-action-toggle dropdown-toggle"
        type="button"
        data-bs-toggle="dropdown"
        data-bs-boundary="viewport"
        data-bs-display="dynamic"
        aria-expanded="false"
      >
        Actions
      </button>

      <ul className="dropdown-menu dropdown-menu-end">
        <li>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => history(`/view-stock/${stock.id}`)}
          >
            <i className="ph ph-eye" /> View
          </button>
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => history(`/edit-stock/${stock.id}`)}
          >
            <i className="ph ph-pencil" /> Edit
          </button>
        </li>
        <li>
          <hr className="dropdown-divider" />
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item text-danger"
            onClick={() => handleDelete(stock.id)}
          >
            <i className="ph ph-trash" /> Delete
          </button>
        </li>
      </ul>
    </div>
  );

  return (
    <div className="stock-list-page">
      <style>
        {`
          .stock-list-page {
            --stock-primary: #111827;
            --stock-muted: #6b7280;
            --stock-border: #e5e7eb;
            --stock-soft: #f9fafb;
            --stock-danger: #b91c1c;
            --stock-success: #166534;
          }

          .stock-list-card {
            border: 1px solid var(--stock-border);
            border-radius: 18px;
            background: #fff;
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
            overflow: visible;
          }

          .stock-list-header {
            padding: 20px 22px;
            border-bottom: 1px solid var(--stock-border);
            background: #fff;
          }

          .stock-header-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          }

          .stock-title-wrap {
            min-width: 0;
          }

          .stock-title {
            margin: 0;
            color: var(--stock-primary);
            font-size: 18px;
            font-weight: 900;
            letter-spacing: -0.02em;
          }

          .stock-subtitle {
            margin: 4px 0 0;
            color: var(--stock-muted);
            font-size: 13px;
          }

          .stock-header-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;
            flex-wrap: wrap;
          }

          .stock-btn {
            min-height: 42px;
            border-radius: 12px;
            padding: 9px 14px;
            font-size: 14px;
            font-weight: 850;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            text-decoration: none;
            border: 1px solid var(--stock-border);
            transition: all .15s ease;
            white-space: nowrap;
          }

          .stock-btn:hover {
            transform: translateY(-1px);
          }

          .stock-btn-outline {
            background: #fff;
            color: var(--stock-primary);
          }

          .stock-btn-outline:hover {
            border-color: var(--stock-primary);
            color: var(--stock-primary);
          }

          .stock-btn-primary {
            background: var(--stock-primary);
            color: #fff !important;
            border-color: var(--stock-primary);
          }

          .stock-btn-primary:hover {
            background: #1f2937;
          }

          .stock-sync-alert {
            border-radius: 14px;
            margin-top: 14px;
            margin-bottom: 0;
            font-size: 13px;
            font-weight: 700;
          }

          .stock-search-section {
            padding: 16px 22px 0;
            background: #fff;
          }

          .stock-search-wrap {
            position: relative;
            max-width: 420px;
          }

          .stock-search-icon {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #9ca3af;
            font-size: 16px;
            z-index: 2;
          }

          .stock-search-input {
            width: 100%;
            min-height: 44px;
            border-radius: 12px;
            border: 1px solid var(--stock-border);
            padding: 9px 38px 9px 40px;
            color: var(--stock-primary);
            font-size: 14px;
            font-weight: 650;
            outline: none;
            transition: all .15s ease;
          }

          .stock-search-input:focus {
            border-color: var(--stock-primary);
            box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
          }

          .stock-search-clear {
            position: absolute;
            right: 9px;
            top: 50%;
            transform: translateY(-50%);
            width: 28px;
            height: 28px;
            border-radius: 999px;
            border: 0;
            background: #f3f4f6;
            color: var(--stock-primary);
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .stock-search-meta {
            margin-top: 8px;
            color: var(--stock-muted);
            font-size: 12px;
            font-weight: 700;
          }

          .desktop-stock-table {
            display: block;
            padding: 16px 22px 22px;
          }

          .mobile-stock-list {
            display: none;
          }

          .stock-table-wrap {
            width: 100%;
            overflow: visible !important;
          }

          .stock-list-page,
          .stock-list-card,
          .stock-table-wrap,
          .dataTables_wrapper,
          .dataTables_scroll,
          .dataTables_scrollHead,
          .dataTables_scrollBody,
          table.dataTable,
          table.dataTable tbody,
          table.dataTable tr,
          table.dataTable td {
            overflow: visible !important;
          }

          .stock-table-wrap {
            position: relative;
            z-index: 1;
          }

          .stock-table-wrap:has(.dropdown-menu.show),
          .dataTables_wrapper:has(.dropdown-menu.show),
          .stock-list-card:has(.dropdown-menu.show) {
            z-index: 99999;
          }

          #dataTable {
            width: 100% !important;
            min-width: 760px;
          }

          #dataTable thead th {
            background: #f9fafb;
            color: #374151;
            font-size: 12px;
            font-weight: 900;
            border-bottom: 1px solid var(--stock-border);
            white-space: nowrap;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            padding: 14px 12px;
          }

          #dataTable tbody td {
            vertical-align: middle;
            color: #4b5563;
            font-size: 13px;
            white-space: nowrap;
            border-bottom: 1px solid #f1f5f9;
            padding: 13px 12px;
          }

          #dataTable tbody tr:hover {
            background: #f9fafb;
          }

          #dataTable tbody tr,
          #dataTable tbody td {
            position: relative;
            z-index: 1;
          }

          #dataTable tbody tr:has(.dropdown-menu.show),
          #dataTable tbody td:has(.dropdown-menu.show) {
            z-index: 1000000 !important;
          }

          #dataTable tbody tr:has(.dropdown-menu.show) ~ tr,
          #dataTable tbody tr:has(.dropdown-menu.show) ~ tr td,
          #dataTable tbody tr:has(.dropdown-menu.show) ~ tr .stock-actions-dropdown {
            z-index: 1 !important;
          }

          .stock-datatable-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            margin-top: 14px;
          }

          .dataTables_wrapper .dataTables_length select {
            border: 1px solid var(--stock-border);
            border-radius: 10px;
            min-height: 36px;
            padding: 6px 9px;
            outline: none;
          }

          .stock-title-cell {
            color: var(--stock-primary);
            font-weight: 900;
            max-width: 280px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .stock-sku-text {
            color: var(--stock-muted);
            font-size: 12px;
            margin-top: 2px;
            max-width: 220px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .stock-qty-pill {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 34px;
            border-radius: 999px;
            background: #f3f4f6;
            border: 1px solid var(--stock-border);
            color: var(--stock-primary);
            font-size: 12px;
            font-weight: 900;
            padding: 5px 9px;
          }

          .stock-track-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 999px;
            padding: 6px 10px;
            font-size: 12px;
            font-weight: 850;
            line-height: 1;
          }

          .stock-track-yes {
            background: #dcfce7;
            color: #166534;
            border: 1px solid #bbf7d0;
          }

          .stock-track-no {
            background: #f3f4f6;
            color: #374151;
            border: 1px solid #e5e7eb;
          }

          .stock-actions-dropdown {
            position: relative;
            z-index: 1;
            display: inline-block;
          }

          .stock-actions-dropdown:has(.dropdown-menu.show) {
            z-index: 1000000 !important;
          }

          .stock-action-toggle {
            border: 1px solid var(--stock-border);
            background: #fff;
            color: var(--stock-primary);
            border-radius: 10px;
            min-height: 36px;
            padding: 7px 11px;
            font-size: 13px;
            font-weight: 850;
          }

          .stock-actions-dropdown .dropdown-menu {
            z-index: 1000001 !important;
            border-radius: 14px;
            border: 1px solid var(--stock-border);
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
            padding: 6px;
          }

          .stock-actions-dropdown .dropdown-menu.show {
            display: block;
            position: absolute !important;
            z-index: 1000001 !important;
            inset: auto 0 auto auto !important;
            transform: translate3d(0, 38px, 0) !important;
            isolation: isolate;
          }

          .stock-actions-dropdown .dropdown-menu.show * {
            position: relative;
            z-index: 1000002;
          }

          .dropdown-item {
            border-radius: 10px;
            font-size: 13px;
            font-weight: 750;
            padding: 9px 12px;
            display: flex;
            align-items: center;
            gap: 7px;
          }

          .dropdown-item:hover {
            background: #f9fafb;
          }

          .stock-mobile-card {
            border: 1px solid var(--stock-border);
            border-radius: 16px;
            background: #fff;
            padding: 15px;
            box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
          }

          .stock-mobile-card + .stock-mobile-card {
            margin-top: 12px;
          }

          .stock-mobile-top {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 12px;
          }

          .stock-mobile-main {
            min-width: 0;
            flex: 1 1 auto;
          }

          .stock-mobile-title {
            color: var(--stock-primary);
            font-size: 15px;
            font-weight: 900;
            line-height: 1.25;
            max-width: 100%;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .stock-mobile-product {
            margin-top: 6px;
            color: var(--stock-muted);
            font-size: 13px;
            font-weight: 700;
            line-height: 1.25;
            max-width: 100%;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .stock-mobile-side {
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            flex: 0 0 auto;
          }

          .stock-mobile-qty {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 38px;
            height: 30px;
            border-radius: 999px;
            background: #f9fafb;
            border: 1px solid var(--stock-border);
            color: var(--stock-primary);
            font-size: 12px;
            font-weight: 900;
          }

          .stock-detail-toggle {
            width: 30px;
            height: 30px;
            border-radius: 999px;
            border: 1px solid var(--stock-border);
            background: #f9fafb;
            color: var(--stock-primary);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            transition: all 0.18s ease;
            padding: 0;
          }

          .stock-detail-toggle:hover {
            background: #f3f4f6;
            border-color: #d1d5db;
          }

          .stock-detail-toggle.is-open {
            background: var(--stock-primary);
            color: #fff;
            border-color: var(--stock-primary);
          }

          .stock-detail-toggle i {
            transition: transform 0.18s ease;
          }

          .stock-detail-toggle.is-open i {
            transform: rotate(180deg);
          }

          .stock-mobile-details {
            display: none;
            padding: 12px 0;
            border-top: 1px solid var(--stock-border);
            border-bottom: 1px solid var(--stock-border);
            animation: stockDetailsSlide 0.18s ease-out;
          }

          .stock-mobile-details.open {
            display: block;
          }

          .stock-mobile-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          @keyframes stockDetailsSlide {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .stock-mobile-label {
            color: var(--stock-muted);
            font-size: 11px;
            font-weight: 850;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin-bottom: 3px;
          }

          .stock-mobile-value {
            color: var(--stock-primary);
            font-size: 13px;
            font-weight: 850;
            word-break: break-word;
          }

          .stock-mobile-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-top: 12px;
          }

          .stock-mobile-price-pill {
            display: inline-flex;
            align-items: center;
            min-height: 38px;
            padding: 8px 12px;
            border-radius: 999px;
            background: #f9fafb;
            border: 1px solid var(--stock-border);
            color: var(--stock-primary);
            font-size: 13px;
            font-weight: 900;
            white-space: nowrap;
            min-width: 0;
            flex: 1 1 auto;
            justify-content: center;
          }

          .mobile-stock-action-dropdown {
            position: relative;
            flex: 0 0 auto;
          }

          .mobile-stock-action-toggle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 40px;
            padding: 9px 13px;
            border-radius: 12px;
            border: 1px solid var(--stock-primary);
            background: var(--stock-primary);
            color: #fff;
            font-size: 13px;
            font-weight: 900;
            box-shadow: 0 8px 18px rgba(17, 24, 39, 0.12);
          }

          .mobile-stock-action-dropdown .dropdown-menu {
            z-index: 99999;
            min-width: 190px;
            padding: 6px;
            border: 1px solid var(--stock-border);
            border-radius: 14px;
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.16);
          }

          .mobile-stock-action-dropdown .dropdown-item {
            display: flex;
            align-items: center;
            gap: 8px;
            border-radius: 10px;
            padding: 10px 12px;
            color: var(--stock-primary);
            font-size: 13px;
            font-weight: 800;
          }

          .stock-mobile-pagination {
            display: none;
            border-top: 1px solid var(--stock-border);
            padding: 14px;
            background: #fff;
          }

          .stock-mobile-pagination-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 12px;
            color: var(--stock-muted);
            font-size: 13px;
            font-weight: 750;
          }

          .stock-mobile-limit-select {
            min-height: 38px;
            border-radius: 10px;
            border: 1px solid var(--stock-border);
            background: #fff;
            color: var(--stock-primary);
            padding: 7px 10px;
            font-size: 13px;
            font-weight: 800;
          }

          .stock-mobile-pagination-controls {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            gap: 8px;
            align-items: center;
          }

          .stock-mobile-page-btn {
            min-height: 40px;
            border-radius: 11px;
            border: 1px solid var(--stock-border);
            background: #fff;
            color: var(--stock-primary);
            font-size: 13px;
            font-weight: 850;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 8px 12px;
          }

          .stock-mobile-page-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .stock-mobile-page-pill {
            min-height: 40px;
            border-radius: 11px;
            border: 1px solid var(--stock-border);
            background: #f9fafb;
            color: var(--stock-primary);
            font-size: 13px;
            font-weight: 900;
            padding: 9px 12px;
            white-space: nowrap;
          }

          @media (max-width: 767.98px) {
            .stock-list-card {
              border-radius: 14px;
              box-shadow: none;
            }

            .stock-list-header {
              padding: 16px;
            }

            .stock-header-row {
              flex-direction: column;
              align-items: stretch;
            }

            .stock-header-actions {
              display: grid;
              grid-template-columns: 1fr;
              gap: 9px;
            }

            .stock-btn {
              width: 100%;
            }

            .stock-search-section {
              padding: 14px 14px 0;
            }

            .stock-search-wrap {
              max-width: 100%;
            }

            .desktop-stock-table {
              display: none;
            }

            .mobile-stock-list {
              display: block;
              padding: 14px;
            }

            .stock-mobile-pagination {
              display: block;
            }

            .stock-mobile-grid {
              grid-template-columns: 1fr;
            }

            .stock-mobile-top {
              gap: 10px;
            }

            .stock-mobile-side {
              align-items: flex-start;
            }

            .stock-mobile-qty {
              max-width: 80px;
            }

            .stock-sync-alert {
              font-size: 12px;
              line-height: 1.35;
            }

            .stock-mobile-pagination-top {
              flex-direction: column;
              align-items: stretch;
            }

            .stock-mobile-limit-select {
              width: 100%;
            }

            .stock-mobile-pagination-controls {
              grid-template-columns: 1fr;
            }

            .stock-mobile-page-pill {
              text-align: center;
            }
          }
        `}
      </style>

      <div className="stock-list-card">
        <div className="stock-list-header">
          <div className="stock-header-row">
            <div className="stock-title-wrap">
              <h5 className="stock-title">Stock Inventory</h5>
              <p className="stock-subtitle">Manage synced CRM products, stock quantities, and prices.</p>
            </div>

            <div className="stock-header-actions">
              {currentUser?.roles?.role === 'admin' && (
                <button
                  className="stock-btn stock-btn-outline"
                  onClick={handleSyncCRM}
                  disabled={syncing}
                  title="Pull latest inventory from GHL CRM and upsert to local DB"
                >
                  {syncing ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <i className="ph ph-arrows-clockwise" />
                      Sync from CRM
                    </>
                  )}
                </button>
              )}

              <button
                className="stock-btn stock-btn-outline"
                onClick={() => setShowExcelModal(true)}
                title="Upload items from Excel"
              >
                <i className="ph ph-upload" /> Upload Excel
              </button>

              <Link className="stock-btn stock-btn-primary" to="/add-stock">
                <i className="ph ph-plus" />
                Add New
              </Link>
            </div>
          </div>

          {syncResult && (
            <div
              className={`alert alert-${syncResult.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show stock-sync-alert`}
              role="alert"
            >
              {syncResult.msg}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSyncResult(null)}
                aria-label="Close"
              />
            </div>
          )}
        </div>

        <div className="stock-search-section">
          <div className="stock-search-wrap">
            <i className="ph ph-magnifying-glass stock-search-icon" />
            <input
              type="text"
              className="stock-search-input"
              placeholder="Search by title, product, SKU, quantity..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="stock-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <i className="ph ph-x" />
              </button>
            )}
          </div>
          <div className="stock-search-meta">
            Showing {filteredJobsData.length} of {jobsData.length} items
          </div>
        </div>

        <div className="desktop-stock-table">
          <div className="stock-table-wrap">
            <table className="table bordered-table mb-0" id="dataTable">
              <thead>
                <tr>
                  <th scope="col">Title / SKU</th>
                  <th scope="col">Product Name</th>
                  <th scope="col">Quantity</th>
                  <th scope="col">Track Inv.</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody />
            </table>
          </div>
        </div>

        <div className="mobile-stock-list">
          {filteredJobsData.length === 0 ? (
            <div className="text-center text-muted py-4">No stock items found.</div>
          ) : (
            paginatedMobileData.map((stock) => {
              const isOpen = !!expandedMobileCards[stock.id];

              return (
                <div className="stock-mobile-card" key={stock.id}>
                  <div className="stock-mobile-top">
                    <div className="stock-mobile-main">
                      <div className="stock-mobile-title" title={stock.title || ''}>
                        {truncateText(stock.title, 55)}
                      </div>

                      {stock.productName && (
                        <div className="stock-mobile-product" title={stock.productName}>
                          {truncateText(stock.productName, 48)}
                        </div>
                      )}
                    </div>

                    <div className="stock-mobile-side">
                      <span className="stock-mobile-qty">Qty {stock.quantity ?? 0}</span>
                      <button
                        type="button"
                        className={`stock-detail-toggle ${isOpen ? 'is-open' : ''}`}
                        onClick={() => toggleMobileDetails(stock.id)}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? 'Hide stock details' : 'Show stock details'}
                      >
                        <i className="ph ph-caret-down" />
                      </button>
                    </div>
                  </div>

                  <div className={`stock-mobile-details ${isOpen ? 'open' : ''}`}>
                    <div className="stock-mobile-grid">
                      <div>
                        <div className="stock-mobile-label">SKU</div>
                        <div className="stock-mobile-value">{stock.sku || 'N/A'}</div>
                      </div>

                      <div>
                        <div className="stock-mobile-label">Product</div>
                        <div className="stock-mobile-value">{truncateText(stock.productName, 40)}</div>
                      </div>

                      <div>
                        <div className="stock-mobile-label">Quantity</div>
                        <div className="stock-mobile-value">{stock.quantity ?? 0}</div>
                      </div>

                      <div>
                        <div className="stock-mobile-label">Track Inventory</div>
                        <div className="stock-mobile-value">{stock.trackInventory ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="stock-mobile-footer">
                    <div className="stock-mobile-price-pill">
                      Sell: {formatMoney(stock.sellPrice)}
                    </div>

                    {renderMobileActions(stock)}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {filteredJobsData.length > 0 && (
          <div className="stock-mobile-pagination">
            <div className="stock-mobile-pagination-top">
              <span>
                Showing {mobileStartIndex + 1} to {mobileEndIndex} of {filteredJobsData.length} items
              </span>

              <select
                className="stock-mobile-limit-select"
                value={mobileLimit}
                onChange={(e) => handleMobileLimitChange(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value} per page
                  </option>
                ))}
              </select>
            </div>

            <div className="stock-mobile-pagination-controls">
              <button
                type="button"
                className="stock-mobile-page-btn"
                onClick={() => {
                  setMobilePage((prev) => Math.max(1, prev - 1));
                  setExpandedMobileCards({});
                }}
                disabled={safeMobilePage === 1}
              >
                <i className="ph ph-caret-left" />
                Previous
              </button>

              <div className="stock-mobile-page-pill">
                Page {safeMobilePage} of {totalMobilePages}
              </div>

              <button
                type="button"
                className="stock-mobile-page-btn"
                onClick={() => {
                  setMobilePage((prev) => Math.min(totalMobilePages, prev + 1));
                  setExpandedMobileCards({});
                }}
                disabled={safeMobilePage === totalMobilePages}
              >
                Next
                <i className="ph ph-caret-right" />
              </button>
            </div>
          </div>
        )}
      </div>
      <ExcelUploadModal show={showExcelModal} onHide={() => setShowExcelModal(false)} />
    </div>
  );
};

export default StockListLayer;
